import { useState } from 'react'
import toast from 'react-hot-toast'
import { inventoryApi } from '../../services/api'
import Button from '../../components/common/Button'

/**
 * Two-step CSV import modal:
 *   Step 1: Upload — user picks a .csv file. We call /preview to parse it and
 *           classify every row (create / update / error) against current
 *           inventory. Nothing is written yet.
 *   Step 2: Review — we show a table of rows with checkboxes. User unchecks
 *           rows they want to skip, clicks Commit, and we POST only the
 *           selected rows to /commit. Server applies them and returns a
 *           summary which we show as a final toast.
 *
 * Headers expected (case-insensitive): ItemNumber, Category, Description, Cost,
 * Quantity. Optional: MedallionType, StockLimit, Year, Color, IsNew.
 *
 * Items are matched by ItemNumber + Year + Color — same key as the unique
 * constraint, same logic as the legacy one-shot importer.
 */
export default function CsvImportModal({ onClose, onImported }) {
    const [step, setStep] = useState('upload')   // 'upload' | 'review'
    const [file, setFile] = useState(null)
    const [previewing, setPreviewing] = useState(false)
    const [committing, setCommitting] = useState(false)

    // Preview state
    const [previewRows, setPreviewRows] = useState([])
    const [previewSummary, setPreviewSummary] = useState(null)
    const [selected, setSelected] = useState(new Set())   // row numbers to commit
    const [statusFilter, setStatusFilter] = useState('all')

    function handleFile(e) {
        const f = e.target.files?.[0] || null
        setFile(f)
    }

    async function handlePreview(e) {
        e?.preventDefault?.()
        if (!file) {
            toast.error('Please choose a CSV file first.')
            return
        }
        setPreviewing(true)
        try {
            const response = await inventoryApi.previewImport(file)
            if (!response.success) {
                toast.error(response.message || 'Preview failed')
                return
            }
            const rows = response.preview.rows || []
            setPreviewRows(rows)
            setPreviewSummary({
                totalRows: response.preview.totalRows,
                toCreate: response.preview.toCreate,
                toUpdate: response.preview.toUpdate,
                errors: response.preview.errors,
            })

            // Default selection: all valid rows (create/update), error rows excluded.
            const defaultSel = new Set(
                rows.filter(r => r.status === 'create' || r.status === 'update')
                    .map(r => r.rowNumber)
            )
            setSelected(defaultSel)
            setStep('review')
        } catch (err) {
            console.error(err)
            toast.error('Network error during preview')
        } finally {
            setPreviewing(false)
        }
    }

    async function handleCommit() {
        const rowsToCommit = previewRows.filter(r => selected.has(r.rowNumber))
        if (rowsToCommit.length === 0) {
            toast.error('Nothing selected to import.')
            return
        }
        setCommitting(true)
        try {
            const response = await inventoryApi.commitImport(rowsToCommit)
            if (!response.success) {
                toast.error(response.message || 'Commit failed')
                return
            }
            const r = response.result
            const errCount = r.errors?.length || 0
            const parts = []
            if (r.rowsCreated) parts.push(`${r.rowsCreated} created`)
            if (r.rowsUpdated) parts.push(`${r.rowsUpdated} updated`)
            if (r.rowsSkipped) parts.push(`${r.rowsSkipped} skipped`)
            if (errCount) parts.push(`${errCount} errors`)
            toast.success('Import done: ' + (parts.join(', ') || 'no changes'))
            onImported?.()
        } catch (err) {
            console.error(err)
            toast.error('Network error during commit')
        } finally {
            setCommitting(false)
        }
    }

    function toggleRow(rowNumber) {
        setSelected(prev => {
            const next = new Set(prev)
            if (next.has(rowNumber)) next.delete(rowNumber)
            else next.add(rowNumber)
            return next
        })
    }

    function toggleAllVisible() {
        const visibleRows = filteredRows.filter(r => r.status !== 'error')
        const allOn = visibleRows.every(r => selected.has(r.rowNumber))
        setSelected(prev => {
            const next = new Set(prev)
            if (allOn) {
                visibleRows.forEach(r => next.delete(r.rowNumber))
            } else {
                visibleRows.forEach(r => next.add(r.rowNumber))
            }
            return next
        })
    }

    const filteredRows = previewRows.filter(r => {
        if (statusFilter === 'all') return true
        return r.status === statusFilter
    })

    const visibleNonError = filteredRows.filter(r => r.status !== 'error')
    const allVisibleSelected = visibleNonError.length > 0 &&
        visibleNonError.every(r => selected.has(r.rowNumber))

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
            onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
            <div className="bg-white rounded-xl shadow-xl max-w-6xl w-full max-h-[90vh] flex flex-col">

                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                    <div>
                        <h3 className="text-lg font-semibold text-ghasc-navy">
                            {step === 'upload' ? 'Import inventory from CSV' : 'Review changes'}
                        </h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                            {step === 'upload'
                                ? 'Step 1 of 2 — upload your file to preview the changes.'
                                : 'Step 2 of 2 — uncheck any rows you want to skip, then click Commit.'}
                        </p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">×</button>
                </div>

                {/* Body */}
                {step === 'upload' ? (
                    <form onSubmit={handlePreview} className="p-6 flex-1 overflow-auto">
                        <div className="space-y-4 max-w-2xl">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">CSV file</label>
                                <input
                                    type="file"
                                    accept=".csv,text/csv"
                                    onChange={handleFile}
                                    className="block w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-ghasc-navy file:text-white hover:file:bg-ghasc-navy/90 file:cursor-pointer cursor-pointer"
                                />
                            </div>

                            <div className="text-sm text-gray-600 bg-gray-50 rounded-lg p-4 space-y-2">
                                <div>
                                    <strong>Required columns:</strong> ItemNumber, Category, Description, Cost, Quantity
                                </div>
                                <div>
                                    <strong>Optional columns:</strong> MedallionType, StockLimit, Year, Color, IsNew
                                </div>
                                <div>
                                    Headers are case-insensitive and order doesn&rsquo;t matter. Items are matched by
                                    <span className="font-mono"> ItemNumber + Year + Color</span>.
                                    Cost can include a dollar sign and commas (<span className="font-mono">$1,234.56</span>) — both are stripped.
                                </div>
                                <div className="text-xs text-gray-500 pt-1">
                                    Nothing is written until you confirm on the next screen.
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end gap-2 pt-6">
                            <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
                            <Button type="submit" disabled={!file || previewing}>
                                {previewing ? 'Analyzing…' : 'Preview changes'}
                            </Button>
                        </div>
                    </form>
                ) : (
                    <>
                        {/* Summary + filter bar */}
                        <div className="px-6 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between flex-wrap gap-3">
                            <div className="flex items-center gap-4 text-sm">
                                <span className="text-gray-700">
                                    <strong>{previewSummary?.totalRows ?? 0}</strong> total
                                </span>
                                <span className="text-green-700">
                                    <strong>{previewSummary?.toCreate ?? 0}</strong> new
                                </span>
                                <span className="text-blue-700">
                                    <strong>{previewSummary?.toUpdate ?? 0}</strong> updates
                                </span>
                                {previewSummary?.errors > 0 && (
                                    <span className="text-red-700">
                                        <strong>{previewSummary.errors}</strong> errors
                                    </span>
                                )}
                                <span className="text-gray-500 border-l border-gray-300 pl-4">
                                    <strong>{selected.size}</strong> selected
                                </span>
                            </div>
                            <div className="flex items-center gap-2 text-sm">
                                <label className="text-gray-600">Filter:</label>
                                <select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    className="px-2 py-1 border border-gray-300 rounded text-sm"
                                >
                                    <option value="all">All</option>
                                    <option value="create">New only</option>
                                    <option value="update">Updates only</option>
                                    <option value="error">Errors only</option>
                                </select>
                            </div>
                        </div>

                        {/* Rows table */}
                        <div className="flex-1 overflow-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-gray-100 sticky top-0 z-10">
                                    <tr>
                                        <th className="w-12 px-3 py-2 text-left">
                                            <input
                                                type="checkbox"
                                                checked={allVisibleSelected}
                                                onChange={toggleAllVisible}
                                                title="Toggle all visible"
                                            />
                                        </th>
                                        <th className="w-16 px-3 py-2 text-left text-xs uppercase tracking-wider text-gray-600">Row</th>
                                        <th className="w-24 px-3 py-2 text-left text-xs uppercase tracking-wider text-gray-600">Status</th>
                                        <th className="px-3 py-2 text-left text-xs uppercase tracking-wider text-gray-600">Item</th>
                                        <th className="px-3 py-2 text-left text-xs uppercase tracking-wider text-gray-600">Description</th>
                                        <th className="px-3 py-2 text-right text-xs uppercase tracking-wider text-gray-600">Cost</th>
                                        <th className="px-3 py-2 text-right text-xs uppercase tracking-wider text-gray-600">Qty</th>
                                        <th className="px-3 py-2 text-left text-xs uppercase tracking-wider text-gray-600">Changes / Notes</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {filteredRows.length === 0 ? (
                                        <tr><td colSpan={8} className="py-8 text-center text-gray-500">No rows match this filter.</td></tr>
                                    ) : (
                                        filteredRows.map(row => (
                                            <PreviewRow
                                                key={row.rowNumber}
                                                row={row}
                                                checked={selected.has(row.rowNumber)}
                                                onToggle={() => toggleRow(row.rowNumber)}
                                            />
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Footer */}
                        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between flex-wrap gap-2">
                            <div className="text-xs text-gray-500">
                                Error rows can&rsquo;t be committed — fix in your spreadsheet and re-upload.
                            </div>
                            <div className="flex gap-2">
                                <Button variant="secondary" onClick={() => { setStep('upload'); setPreviewRows([]); setSelected(new Set()) }}>
                                    Back
                                </Button>
                                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                                <Button onClick={handleCommit} disabled={selected.size === 0 || committing}>
                                    {committing ? 'Importing…' : `Commit ${selected.size} row${selected.size === 1 ? '' : 's'}`}
                                </Button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}

/**
 * One row in the preview table. Shows the new values, plus inline "was X → Y"
 * diffs for the fields that are changing on updates.
 */
function PreviewRow({ row, checked, onToggle }) {
    const isError = row.status === 'error'
    const isUpdate = row.status === 'update'

    // Build a list of changed fields for update rows
    let changeNotes = null
    if (isUpdate && row.existingItem) {
        const ex = row.existingItem
        const diffs = []
        const fmt = (v) => (v === null || v === undefined || v === '') ? '—' : String(v)
        if (Number(ex.cost) !== Number(row.cost)) diffs.push(`cost: $${Number(ex.cost).toFixed(2)} → $${Number(row.cost).toFixed(2)}`)
        if (Number(ex.quantity) !== Number(row.quantity)) diffs.push(`qty: ${ex.quantity} → ${row.quantity}`)
        if ((ex.description || '') !== (row.description || '')) diffs.push('description changed')
        if ((ex.category || '') !== (row.category || '')) diffs.push(`category: ${fmt(ex.category)} → ${fmt(row.category)}`)
        if ((ex.medallionType || '') !== (row.medallionType || '')) diffs.push(`medallionType: ${fmt(ex.medallionType)} → ${fmt(row.medallionType)}`)
        if (Number(ex.stockLimit) !== Number(row.stockLimit)) diffs.push(`stockLimit: ${ex.stockLimit} → ${row.stockLimit}`)
        if (Boolean(ex.isNew) !== Boolean(row.isNew)) diffs.push(`isNew: ${ex.isNew} → ${row.isNew}`)
        changeNotes = diffs.length === 0 ? <span className="text-gray-400">no changes</span> : diffs.join('; ')
    }

    const statusBadge = isError ? (
        <span className="inline-block px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">error</span>
    ) : isUpdate ? (
        <span className="inline-block px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">update</span>
    ) : (
        <span className="inline-block px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">new</span>
    )

    const variant = [row.year, row.color].filter(Boolean).join(' / ')

    return (
        <tr className={isError ? 'bg-red-50/30' : ''}>
            <td className="px-3 py-2">
                {!isError && (
                    <input type="checkbox" checked={checked} onChange={onToggle} />
                )}
            </td>
            <td className="px-3 py-2 text-gray-500 font-mono text-xs">{row.rowNumber}</td>
            <td className="px-3 py-2">{statusBadge}</td>
            <td className="px-3 py-2">
                <div className="font-mono text-xs text-gray-700">{row.itemNumber || '—'}</div>
                {variant && <div className="text-xs text-gray-400">{variant}</div>}
            </td>
            <td className="px-3 py-2">
                <div className="text-gray-900 truncate max-w-xs" title={row.description}>{row.description}</div>
                {row.category && <div className="text-xs text-gray-400">{row.category}</div>}
            </td>
            <td className="px-3 py-2 text-right text-gray-900">${Number(row.cost || 0).toFixed(2)}</td>
            <td className="px-3 py-2 text-right text-gray-900">{row.quantity ?? '—'}</td>
            <td className="px-3 py-2 text-xs text-gray-600">
                {isError ? (
                    <span className="text-red-700">{row.errorMessage}</span>
                ) : isUpdate ? (
                    changeNotes
                ) : (
                    <span className="text-gray-400">will be created</span>
                )}
            </td>
        </tr>
    )
}